"use client";

import { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const manifestoItems = [{
  question: "What file formats do you accept for engraving?",
  answer: "We prefer vector files like .DXF, .DWG, or .AI for precision. If you only have a PDF or a physical part, our engineering team can digitize your specifications to ensure the final mark meets your exact tolerance requirements.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/evermark_file_prep.png",
  imageAlt: "Engineer reviewing CAD file on monitor"
}, {
  question: "How do you ensure compliance with industry standards?",
  answer: "Every project is cross-referenced against your provided specifications, whether it is UL 508A, MIL-SPEC, or internal plant standards. We provide full documentation and material certification with every shipment to ensure your audit trail is complete.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/evermark_compliance_docs.png",
  imageAlt: "Compliance documentation and laser marked tag"
}, {
  question: "What is your turnaround time for custom orders?",
  answer: "Standard orders typically ship within 3 to 5 business days. For urgent plant shutdowns or critical maintenance projects, we offer expedited processing to get your identification tags on-site and installed as quickly as possible.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/evermark_production_floor.webp",
  imageAlt: "Laser marking station in industrial facility"
}, {
  question: "Can you handle large-scale asset tagging projects?",
  answer: "Yes. We specialize in high-volume runs for facility-wide upgrades. Whether you need 50 or 5,000 valve tags, our automated systems maintain consistent depth and legibility across every single piece in the batch.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/evermark_batch_processing.png",
  imageAlt: "Rows of stainless steel asset tags"
}, {
  question: "What materials do you work with?",
  answer: "We process a wide range of industrial substrates including stainless steel, anodized aluminum, brass, phenolic, and two-color acrylic. Each material is selected based on your environment's chemical, thermal, and UV exposure needs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/evermark_material_samples.png",
  imageAlt: "Assorted industrial marking materials"
}, {
  question: "Do you provide submittal packages for approval?",
  answer: "We provide comprehensive submittal packages including material data sheets, layout proofs, and finish samples. We do not begin production until you have reviewed and signed off on the final design specifications.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/evermark_submittal_review.webp",
  imageAlt: "Submittal package folder on drafting table"
}, {
  question: "How are tags packaged for shipping?",
  answer: "We organize and label all tags by room, panel, or equipment ID to simplify your installation process. Everything is securely packed to prevent scratching or damage during transit, ensuring they arrive ready for immediate mounting.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/evermark_shipping_prep.png",
  imageAlt: "Organized tags ready for shipment"
}, {
  question: "Are you a veteran-owned business?",
  answer: "Yes, Evermark Industrial Solutions is a veteran-owned company. We bring the same discipline, attention to detail, and commitment to mission success to every marking project we undertake for our industrial partners.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/evermark_veteran_owned.png",
  imageAlt: "Evermark facility exterior"
}];
export default function () {
  const [openValue, setOpenValue] = useState<string>("item-0");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1664" id="datacenterfaq" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-16 lg:px-22">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5 relative">
            <div ref={headerRef} className={`lg:sticky lg:top-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <div aria-hidden="true" className="pointer-events-none absolute -top-10 -left-4 lg:-left-8 select-none font-default font-light leading-none text-foreground/5" style={{
              fontSize: "20vw"
            }}>
                05
              </div>

              {}
              <div className="relative flex items-center gap-3 mb-8">
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
                  § 05 / EVERMARK — TECHNICAL FAQ
                </span>
              </div>

              {}
              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-primary" />
                <div className="h-1 w-1 bg-primary" />
              </div>

              {}
              <h2 className="relative font-default font-bold leading-[1.0] tracking-tight text-foreground mb-8" style={{
              fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
            }}>
                Technical{" "}
                <span className="italic text-primary">Specifications</span>
              </h2>

              {}
              <p className="relative font-default text-foreground/85 leading-relaxed mb-10 max-w-md" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
            }}>
                Eight core answers. One standard of quality. Permanent. Compliant.
              </p>

              {}
              <Link to="/ContactPage" className="relative inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-primary group">
                <span className="relative">
                  Request a Quote
                  <span className="absolute left-0 -bottom-1 h-px bg-primary transition-all duration-300 w-[60%] group-hover:w-full" />
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible value={openValue} onValueChange={v => setOpenValue(v)} className="w-full">
              {manifestoItems.map((item, i) => {
              const itemValue = `item-${i}`;
              const isOpen = openValue === itemValue;
              const indexLabel = `${String(i + 1).padStart(2, "0")} / ${String(manifestoItems.length).padStart(2, "0")}`;
              return <AccordionItem key={i} data-index={i} value={itemValue} className="relative border-b-0 border-t border-foreground/20">
                    {}
                    <span aria-hidden="true" className={`pointer-events-none absolute left-0 top-0 w-[2px] bg-primary transition-all duration-300 ease-out ${isOpen ? "h-full opacity-100" : "h-0 opacity-0"}`} />

                    <AccordionTrigger className={`group flex items-center justify-between gap-6 py-6 hover:no-underline rounded-md transition-colors duration-300 [&>svg]:hidden ${isOpen ? "text-foreground" : "text-foreground/85"}`}>
                      <span className="flex-1 text-left font-default font-semibold leading-tight tracking-tight" style={{
                    fontSize: "clamp(1.125rem, 1.8vw, 1.5rem)"
                  }}>
                        {item.question}
                      </span>
                      <span className="hidden sm:block font-mono text-xs uppercase tracking-[0.18em] text-foreground/55 shrink-0">
                        {indexLabel}
                      </span>
                      <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 text-primary transition-all duration-300 ease-out ${isOpen ? "rotate-45 opacity-100" : "rotate-0 opacity-70 group-hover:opacity-100"}`} aria-hidden="true">
                        <Plus className="h-5 w-5" strokeWidth={1.25} />
                      </span>
                    </AccordionTrigger>

                    <AccordionContent className="pb-8 pt-2 pl-0 md:pl-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        <p className="md:col-span-2 font-default text-foreground/85 leading-relaxed" style={{
                      fontSize: "0.9375rem",
                      lineHeight: 1.7
                    }}>
                          {item.answer}
                        </p>

                        {}
                        <div className="hidden md:block relative">
                          <div className="relative border border-foreground/15 aspect-[4/5] overflow-hidden">
                            {}
                            <span aria-hidden="true" className="absolute top-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute top-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-primary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-primary" />
                            </span>

                            <img src={item.image} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]" />
                          </div>
                          <div className="mt-3 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55">
                            FIG. {String(i + 1).padStart(2, "0")} — DETAIL
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}

              {}
              <div className="relative border-t border-foreground/20 pt-6 flex items-center gap-2">
                <div className="h-1 w-1 bg-primary" />
                <div className="h-px w-16 bg-primary" />
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55 ml-2">
                  End of technical faq
                </span>
              </div>
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}